import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Building2, Briefcase, CalendarDays, CheckCircle2, Clock, Mail, Phone, User, X } from "lucide-react";
import { SLOT_TIMES, buildDays, isTaken } from "./slotUtils";

const API = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

const EMPTY_FORM = { full_name: "", position: "", work_email: "", phone: "", company: "" };

export default function DemoBookingDrawer() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [day, setDay] = useState(null);
    const [slot, setSlot] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const days = useMemo(buildDays, []);

    // Intercept every "Book a demo" link site-wide and open this drawer instead.
    useEffect(() => {
        const onClick = (e) => {
            const a = e.target.closest?.('a[href^="mailto:enquires@tedopsai.com?subject=Book"]');
            if (a) {
                e.preventDefault();
                setOpen(true);
            }
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const canSubmit =
        form.full_name.trim().length >= 2 &&
        /.+@.+\..+/.test(form.work_email) &&
        form.phone.trim().length >= 5 &&
        form.company.trim().length >= 2 &&
        day &&
        slot &&
        !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError("");
        try {
            const res = await fetch(`${API}/api/demo-bookings`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, day: day.iso, slot }),
            });
            if (!res.ok) throw new Error("Request failed");
            setDone(true);
        } catch {
            setError("Something went wrong sending your request. Please try again, or email enquires@tedopsai.com.");
        } finally {
            setSubmitting(false);
        }
    };

    const close = () => {
        setOpen(false);
        setTimeout(() => {
            setDone(false);
            setError("");
        }, 300);
    };

    const inputCls =
        "w-full rounded-xl border border-[#0F172A]/10 bg-[#F7F8FA] px-4 py-3 pl-11 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors duration-200 focus:border-[#0066FF]/50 focus:bg-white";

    const selectedLabel = day && slot ? `${day.weekday} ${day.label} · ${slot}` : null;

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={close}
                        className="fixed inset-0 z-[100] bg-[#060A12]/60 backdrop-blur-sm"
                        data-testid="demo-booking-overlay"
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="fixed right-0 top-0 z-[101] flex h-full w-full max-w-md flex-col border-l border-[#0F172A]/[0.08] bg-white"
                        style={{ boxShadow: "-40px 0 120px -40px rgba(11,18,32,0.3)" }}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Book a demo"
                        data-testid="demo-booking-drawer"
                    >
                        <div className="flex items-start justify-between border-b border-[#0F172A]/[0.06] px-6 py-5 sm:px-8">
                            <div>
                                <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#0066FF]">
                                    Book a Demo
                                </p>
                                <h2 className="mt-1.5 font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">
                                    See TedOps AI in Action
                                </h2>
                                <p className="mt-1 text-sm text-[#64748B]">
                                    Pick a day and time — we'll confirm by email.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={close}
                                aria-label="Close booking panel"
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#0F172A]/10 text-[#64748B] transition-colors duration-200 hover:border-[#0066FF]/40 hover:text-[#0066FF]"
                                data-testid="demo-booking-close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {done ? (
                            <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="demo-booking-success">
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981]/10">
                                    <CheckCircle2 className="h-8 w-8 text-[#10B981]" strokeWidth={1.75} />
                                </span>
                                <p className="mt-6 font-display text-xl font-extrabold text-[#0F172A]">Request received</p>
                                <p className="mt-2 max-w-xs text-sm leading-relaxed text-[#64748B]">
                                    Thanks{form.full_name ? `, ${form.full_name.split(" ")[0]}` : ""} — we've
                                    got your request for{" "}
                                    <span className="font-bold text-[#0F172A]">{selectedLabel} (UK time)</span>.
                                    We'll confirm your demo by email shortly.
                                </p>
                                <button
                                    type="button"
                                    onClick={close}
                                    className="mt-8 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-8 py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                                    data-testid="demo-booking-done"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-6 sm:px-8" data-testid="demo-booking-form">
                                <div className="space-y-4">
                                    <div className="relative">
                                        <User className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
                                        <input
                                            required
                                            value={form.full_name}
                                            onChange={set("full_name")}
                                            placeholder="Full name *"
                                            className={inputCls}
                                            data-testid="demo-booking-name"
                                        />
                                    </div>
                                    <div className="relative">
                                        <Briefcase className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
                                        <input
                                            value={form.position}
                                            onChange={set("position")}
                                            placeholder="Position (e.g. Operations Manager)"
                                            className={inputCls}
                                            data-testid="demo-booking-position"
                                        />
                                    </div>
                                    <div className="relative">
                                        <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
                                        <input
                                            required
                                            type="email"
                                            value={form.work_email}
                                            onChange={set("work_email")}
                                            placeholder="Email address *"
                                            className={inputCls}
                                            data-testid="demo-booking-email"
                                        />
                                    </div>
                                    <div className="relative">
                                        <Phone className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
                                        <input
                                            required
                                            type="tel"
                                            value={form.phone}
                                            onChange={set("phone")}
                                            placeholder="Telephone number *"
                                            className={inputCls}
                                            data-testid="demo-booking-phone"
                                        />
                                    </div>
                                    <div className="relative">
                                        <Building2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
                                        <input
                                            required
                                            value={form.company}
                                            onChange={set("company")}
                                            placeholder="Company *"
                                            className={inputCls}
                                            data-testid="demo-booking-company"
                                        />
                                    </div>
                                </div>

                                <div className="mt-7">
                                    <p className="flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                                        <CalendarDays className="h-3.5 w-3.5 text-[#0066FF]" />
                                        Choose a day
                                    </p>
                                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="demo-booking-days">
                                        {days.map((d) => (
                                            <button
                                                key={d.iso}
                                                type="button"
                                                onClick={() => setDay(d)}
                                                className={`flex w-[68px] shrink-0 flex-col items-center rounded-xl border px-2 py-2.5 transition-colors duration-200 ${
                                                    day?.iso === d.iso
                                                        ? "border-[#0066FF] bg-[#F0F7FF] text-[#0F172A]"
                                                        : "border-[#0F172A]/10 bg-[#F7F8FA] text-[#64748B] hover:border-[#0066FF]/40 hover:text-[#0F172A]"
                                                }`}
                                                data-testid={`demo-day-${d.iso}`}
                                            >
                                                <span className="text-[10px] font-bold uppercase tracking-wider">{d.weekday}</span>
                                                <span className="mt-0.5 text-xs font-semibold">{d.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <p className="flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                                        <Clock className="h-3.5 w-3.5 text-[#0066FF]" />
                                        Choose a time <span className="normal-case tracking-normal">(UK)</span>
                                    </p>
                                    <div className="mt-3 grid grid-cols-4 gap-2" data-testid="demo-booking-slots">
                                        {SLOT_TIMES.map((t, i) => {
                                            const taken = day ? isTaken(day.iso, i) : false;
                                            const selected = slot === t;
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    disabled={!day || taken}
                                                    onClick={() => setSlot(t)}
                                                    className={`rounded-lg border px-1 py-2 text-xs font-semibold transition-colors duration-200 ${
                                                        taken
                                                            ? "cursor-not-allowed border-[#0F172A]/[0.05] bg-[#F7F8FA] text-[#94A3B8] line-through"
                                                            : selected
                                                              ? "border-[#0066FF] bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] text-white"
                                                              : "border-[#0F172A]/10 bg-white text-[#334155] hover:border-[#0066FF]/40 hover:text-[#0066FF]"
                                                    } ${!day ? "opacity-40" : ""}`}
                                                    data-testid={`demo-slot-${t.replace(":", "")}`}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-3 flex items-center gap-5 text-[10px] font-semibold text-[#64748B]">
                                        <span className="flex items-center gap-1.5">
                                            <span className="h-2 w-2 rounded-full bg-[#0066FF]" /> Available
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="h-2 w-2 rounded-full bg-[#94A3B8]" /> Booked
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-7 border-t border-[#0F172A]/[0.06] pt-5">
                                    {selectedLabel && (
                                        <p className="mb-4 rounded-xl border border-[#0066FF]/25 bg-[#F0F7FF] px-4 py-2.5 text-center text-sm font-bold text-[#0F172A]" data-testid="demo-booking-selection">
                                            {selectedLabel} · UK time
                                        </p>
                                    )}
                                    {error && (
                                        <p className="mb-4 rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/[0.06] px-4 py-2.5 text-sm text-[#DC2626]" data-testid="demo-booking-error">
                                            {error}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={!canSubmit}
                                        className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-7 py-4 text-xs font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 enabled:hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40"
                                        data-testid="demo-booking-submit"
                                    >
                                        {submitting ? "Sending…" : "Request Demo"}
                                        {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                                    </button>
                                    <p className="mt-3 text-center text-[10.5px] leading-relaxed text-[#94A3B8]">
                                        All times UK (GMT). We'll confirm your demo by email — no payment
                                        or commitment needed.
                                    </p>
                                </div>
                            </form>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
